import { Add, Close, Download, MailOutline, Schedule } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { MarkItViewApi } from '../../../Apis/Apis';
import { ContractDate, EmailScheduleModel, MarkItViewQuerySavedParameters, MarkItViewSavedQueryModel } from '../../../Generated/Mark-It-View-Api';
import { describeEmailSchedule, emailScheduleDraftFromModel, toUpsertEmailScheduleRequest } from '../EmailSchedules/EmailScheduleDraft';
import { useLoadingState, useMtmDate } from '../../../Hooks';
import { MarkItViewQueryParameters } from '../../../Types/MarkItViewQueryParameters';
import { MarkItViewSavedQuerySet } from '../../../Types/MarkItViewSavedQuerySet';
import StoneXMainPage from '../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXAutocomplete, StoneXButton } from '../../StoneXMui';
import './MarketView.scss';
import { QueryPage } from './QueryPage/QueryPage';
import SendSavedQueryEmailDialog from './SendSavedQueryEmailDialog';
import { StoneXRow, StoneXTab, StoneXTabs } from '../../StoneX';

interface QueryPageData {
  id: number;
  title: string;
  queryPage: ReactElement;
}

interface MarketViewProps {
}

export default function MarketViewSolution(props: MarketViewProps) {
  // const [queryPages, setQueryPages] = useState<ReactElement[]>([]);
  const [queryPages, setQueryPages] = useState<QueryPageData[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const querySaveLoadingState = useLoadingState();
  // const savedQuerySets = useListResource<MarkItViewSavedQueryModel, string>([], null, (item) => item.name);
  const savedQuerySetsLoadingState = useLoadingState();
  const querySetDeleteLoadingState = useLoadingState();
  const [savedQuerySets, setSavedQuerySets] = useState<MarkItViewSavedQueryModel[]>([]);
  const [savedQuerySetToDelete, setSavedQuerySetToDelete] = useState<MarkItViewSavedQueryModel | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [exportingSavedQueryId, setExportingSavedQueryId] = useState<number | null>(null);
  const [savedQueryToEmail, setSavedQueryToEmail] = useState<MarkItViewSavedQueryModel | null>(null);

  const loadSavedQuerySets = (): void => {
    MarkItViewApi.listMarketViewSavedQuery(savedQuerySetsLoadingState.setLoadingState).then((q) => setSavedQuerySets(q.data.rows ?? []));
  };

  const downloadSavedQuery = (savedQuery: MarkItViewSavedQueryModel): void => {
    setExportingSavedQueryId(savedQuery.savedQueryId);
    MarkItViewApi.exportMarketViewSavedQuery(undefined, String(savedQuery.savedQueryId), { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(response.data as unknown as Blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${savedQuery.name}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error('Failed to export saved query', error))
      .finally(() => setExportingSavedQueryId(null));
  };

  const newQueryPage = (savedQueryToLoad?: MarkItViewSavedQuerySet) => {
    const newPage = (
      <QueryPage id={pageCount} onTitleChange={onTitleChange} save={(q: MarkItViewSavedQuerySet) => saveQuerySet(q)} querySet={savedQueryToLoad} />
    );
    const newPageData = { id: pageCount, title: `Query ${pageCount}`, queryPage: newPage };
    const newQueryPages = [...queryPages, newPageData];
    setQueryPages(newQueryPages);

    setActiveTabIndex(newQueryPages.length - 1);

    setPageCount(pageCount + 1);
  };

  const onTitleChange = (id: number, newTitle: string) => {
    setQueryPages((previous) => previous.map((q) => (q.id == id ? { ...q, title: newTitle } : q)));
  };

  const onTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setActiveTabIndex(newTabIndex);
  };
  
  function handleTabChange(newIndex: number) {
    setActiveTabIndex(newIndex);
  }


  // StoneXContractMonthPicker reports 0-based months; saved queries store 1-based months, which the picker
  // converts back when a saved query is loaded. Copy instead of mutating the live form value so a save
  // does not shift the months of the query that is still on screen.
  const toSavedContractDate = (contractDate: ContractDate | null | undefined): ContractDate | null =>
    contractDate?.month != null ? { year: contractDate.year, month: contractDate.month + 1 } : (contractDate ?? null);

  const onLoadQueryChange = (e: any, item: MarkItViewSavedQueryModel | null) => {
    const allParams = item?.queriesParameters ?? [];
    const matrixEntry = allParams.find((q) => q.priceQueryType === 'Matrix');
    const regularParams = allParams.filter((q) => q.priceQueryType !== 'Matrix');

    const savedQuerySet = {
      id: item?.savedQueryId,
      name: item?.name,
      emailSchedule: emailScheduleDraftFromModel(item?.emailSchedule),
      matrixParameters: (matrixEntry as any)?.matrixParameters ?? null,
      querySetParameters: regularParams.map((q) => {
        return {
          ...q,
          startQuoteDate: dayjs(useMtmDate())
            .subtract(q.daysToLookback ? -q.daysToLookback : 1, q.daysToLookback ? 'day' : 'year')
            .toDate(),
          endQuoteDate: useMtmDate(),
          mtmDate: useMtmDate(),
        } as MarkItViewQueryParameters;
      }),
    } as MarkItViewSavedQuerySet;

    newQueryPage(savedQuerySet);
  };

  const deleteTab = (indexToDelete: number) => {
    if (queryPages.length == 1) {
      // setActiveTabIndex(0);
      // newQueryPage();
      return;
    } else if (queryPages.length - 1 == activeTabIndex) {
      setActiveTabIndex(activeTabIndex - 1);
    }

    setQueryPages((previous) => previous.filter((q, i) => i !== indexToDelete));
  };

  const retrieveSavedQuerySets = (): void => {};

  const loadSavedQuerySet = (query: MarkItViewSavedQuerySet): void => {};

  const saveQuerySet = (query: MarkItViewSavedQuerySet): Promise<void> => {
    const newItemToSave = query.querySetParameters.map((q) => {
      let daysToLookback: number | null = null;

      if (q.startQuoteDate && q.endQuoteDate) {
        daysToLookback = dayjs(q.startQuoteDate).diff(q.endQuoteDate, 'day');
      }

      return {
        id: q.id,
        daysToLookback: daysToLookback,
        priceQueryType: q.priceQueryType,
        years: q.years,
        months: q.months,
        commodityType: q.commodityType,
        productId: q.productId,
        startContractDate: toSavedContractDate(q.startContractDate),
        endContractDate: toSavedContractDate(q.endContractDate),
        monthsAhead: q.monthsAhead,
      } as MarkItViewQuerySavedParameters;
    });

    // Include matrix parameters as a special entry
    if (query.matrixParameters) {
      const matrixParameters = query.matrixParameters;
      matrixParameters.startDate = undefined;
      matrixParameters.endDate = undefined;
      newItemToSave.push({
        id: -1,
        priceQueryType: 'Matrix',
        years: [],
        months: [],
        startContractDate: null,
        endContractDate: null,
        matrixParameters: matrixParameters,
      } as any);
    }

    const promise = MarkItViewApi.addMarketViewSavedQuery(querySaveLoadingState.setLoadingState, query.id, query.name, newItemToSave)
      .then(async (response) => {
        const newItem = response.data.savedQuery!;
        const existingSchedule = (savedQuerySets.find((q) => q.savedQueryId === newItem.savedQueryId)?.emailSchedule ?? null) as EmailScheduleModel | null;
        newItem.emailSchedule = (await saveEmailSchedule(newItem.savedQueryId, query, existingSchedule)) as EmailScheduleModel;

        setSavedQuerySets((previous) => {
          return [...previous.filter((q) => q.savedQueryId != newItem.savedQueryId), newItem];
        });
      });

    return promise;
  };

  const saveEmailSchedule = async (
    savedQueryId: number,
    query: MarkItViewSavedQuerySet,
    existingSchedule: EmailScheduleModel | null,
  ): Promise<EmailScheduleModel | null> => {
    const draft = query.emailSchedule;

    if (draft?.enabled) {
      const response = await MarkItViewApi.upsertEmailSchedule(undefined, toUpsertEmailScheduleRequest({ ...draft, emailScheduleId: existingSchedule?.emailScheduleId ?? draft.emailScheduleId }, savedQueryId));
      return response.data.emailSchedule ?? null;
    }

    if (existingSchedule) {
      await MarkItViewApi.deleteEmailSchedule(undefined, String(existingSchedule.emailScheduleId));
    }

    return null;
  };

  const deleteQuerySet = (): void => {
    if (!savedQuerySetToDelete) {
      return;
    }

    const savedQueryId = savedQuerySetToDelete.savedQueryId;

    setDeleteFailed(false);
    MarkItViewApi.deleteMarketViewSavedQuery(querySetDeleteLoadingState.setLoadingState, String(savedQueryId))
      .then(() => {
        setSavedQuerySets((previous) => previous.filter((q) => q.savedQueryId !== savedQueryId));
        setSavedQuerySetToDelete(null);
      })
      .catch(() => setDeleteFailed(true));
  };

  const closeDeleteDialog = (): void => {
    setSavedQuerySetToDelete(null);
    setDeleteFailed(false);
  };

  const renderDeleteQuerySetDialog = (): ReactNode => {
    return (
      <Dialog open={!!savedQuerySetToDelete} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Saved Query</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete "{savedQuerySetToDelete?.name}"? This cannot be undone.</DialogContentText>
          {deleteFailed && <DialogContentText color="error">An error occurred while deleting this query.</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <StoneXButton onClick={deleteQuerySet} isWorking={querySetDeleteLoadingState.isLoading()}>
            Delete
          </StoneXButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderTabs = (): ReactNode[] => {
    return queryPages.map((queryPage, index) => {
      return (
        <Tab
          sx={{ maxWidth: 300 }}
          label={
            <span style={{ width: '100%', display: 'inline-flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{queryPage.title}</span>
              <Close
                fontSize="small"
                sx={{ color: 'gray', '&:hover': { color: 'inherit' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteTab(index);
                }}
              />
            </span>
          }
          key={queryPage.id}
        />
      );
    });
  };

  useEffect(() => {
    loadSavedQuerySets();
    if (queryPages.length == 0) {
      newQueryPage();
    }
  }, []);

  const MarkItViewQueryTabs = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', margin: '0px 10px' }}>
        <StoneXTabs activeTabIndex={activeTabIndex}>
          {queryPages.map((queryPage, index) => {
            return <StoneXTab key={index} onClick={() => handleTabChange(index)} onClose={() => deleteTab(index)} label={queryPage.title} isActive={activeTabIndex == index} showCloseButton/>
          })}
          <StoneXTab key={-1} onClick={newQueryPage} label={<IconButton><Add fontSize="medium" /></IconButton>}></StoneXTab>
        </StoneXTabs>
      </div>
    );
  };

  return (
    <StoneXMainPage>
      <StoneXRow align='end'>
        <h1>Mark-It View</h1>
        
        <div className='pull-right'>
        <StoneXAutocomplete
            options={savedQuerySets}
            getOptionLabel={(savedQuery) => savedQuery!.name}
            getOptionKey={(savedQuery) => savedQuery.savedQueryId}
            loading={savedQuerySetsLoadingState.isLoading}
            value={null}
            variant="standard"
            placeholder='Load Saved Query'
            label=""
            onChange={onLoadQueryChange}
            renderOption={(optionProps, savedQuery) => (
              <Box
                component="li"
                {...optionProps}
                key={savedQuery.savedQueryId}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  '& .saved-query-action': { visibility: 'hidden' },
                  '&:hover .saved-query-action, &:focus-within .saved-query-action': { visibility: 'visible' },
                }}
              >
                <span style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{savedQuery.name}</span>
                {savedQuery.emailSchedule && (
                  <Tooltip title={`Emailed: ${describeEmailSchedule(savedQuery.emailSchedule)}`}>
                    <Schedule fontSize="small" sx={{ flexShrink: 0, color: savedQuery.emailSchedule.lastRunError ? 'error.main' : 'gray' }} />
                  </Tooltip>
                )}
                <Tooltip title="Send by email now">
                  <IconButton
                    className="saved-query-action"
                    aria-label={`Email ${savedQuery.name}`}
                    size="small"
                    sx={{ padding: 0, flexShrink: 0, color: 'gray', '&:hover': { color: 'inherit' } }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSavedQueryToEmail(savedQuery);
                    }}
                  >
                    <MailOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as Excel">
                  <IconButton
                    className="saved-query-action"
                    aria-label={`Download ${savedQuery.name}`}
                    size="small"
                    disabled={exportingSavedQueryId === savedQuery.savedQueryId}
                    sx={{ padding: 0, flexShrink: 0, color: 'gray', '&:hover': { color: 'inherit' } }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadSavedQuery(savedQuery);
                    }}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete saved query">
                  <IconButton
                    className="saved-query-action"
                    aria-label={`Delete ${savedQuery.name}`}
                    size="small"
                    sx={{ padding: 0, flexShrink: 0, color: 'gray', '&:hover': { color: 'inherit' } }}
                    onMouseDown={(e) => {
                      // Keep focus in the input so the popup stays mounted and the click fires.
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteFailed(false);
                      setSavedQuerySetToDelete(savedQuery);
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            sx={{
              '& .MuiAutocomplete-input::placeholder': {
                opacity: 1,
              },
            }}
          />
        </div>

      </StoneXRow>

      {renderDeleteQuerySetDialog()}
      <SendSavedQueryEmailDialog savedQuery={savedQueryToEmail} onClose={() => setSavedQueryToEmail(null)} />
      
      <MarkItViewQueryTabs />

      {queryPages.map((q, i) => (
        <Box key={q.id} sx={{ display: activeTabIndex == i ? 'block' : 'none' }}>
          {q.queryPage}
        </Box>
      ))}
      
    </StoneXMainPage>
  );
}
